import type { Component } from "vue";
import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
} from "vue-router";
import type { RouteRecordRaw } from "vue-router";

import { applyHead } from "../seo/head";
import { PAGES, pageByName } from "../seo/pages";
import { SITE_URL } from "../site";

/**
 * Real routes, which is the point of this port.
 *
 * The old site loaded HTML fragments into <main> by AJAX and never changed the
 * address, so every page shared one URL: no deep link worked, the back button
 * did nothing, and a search engine only ever saw the start page. Each entry
 * here is a URL a visitor can type, bookmark and share, and that Google can
 * index on its own.
 *
 * The paths are German because they end up in the address bar of people who
 * read German. The components are loaded lazily so a visitor who only looks at
 * the start page does not download the contact form.
 */
const COMPONENTS: Record<string, () => Promise<Component>> = {
  start: () => import("../views/HomeView.vue"),
  auftritte: () => import("../views/ShowsView.vue"),
  medien: () => import("../views/MediaView.vue"),
  ueberUns: () => import("../views/AboutView.vue"),
  kontakt: () => import("../views/ContactView.vue"),
  impressum: () => import("../views/LegalView.vue"),
  nichtGefunden: () => import("../views/NotFoundView.vue"),
};

export const routes: RouteRecordRaw[] = PAGES.map((page) => ({
  path: page.path,
  name: page.name,
  component: COMPONENTS[page.name],
  meta: { page: page.name },
}));

export function createAppRouter() {
  const router = createRouter({
    // Real browser history, so /auftritte is an address and not a fragment.
    // The prerender step runs the same router in node, where there is no
    // window to keep history in; vite replaces import.meta.env.SSR with a
    // constant, so only one of the two branches survives into each bundle.
    history: import.meta.env.SSR
      ? createMemoryHistory()
      : createWebHistory("/"),
    routes,
    // A new page starts at the top; going back returns to where you were. The
    // fragment router had neither.
    scrollBehavior(_to, _from, saved) {
      return saved ?? { top: 0 };
    },
  });

  router.afterEach((to) => {
    // The prerender writes the head of each page into the HTML file itself,
    // from the same table - see scripts/prerender.mjs. There is no document
    // to write into here.
    if (import.meta.env.SSR) {
      return;
    }
    applyHead(pageByName(String(to.meta.page)), SITE_URL);
  });

  return router;
}
